import { NotificationBase, NotificationStatus } from '../types/Notification'

/**
 * Check if the browser supports HTML5 notifications
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window
}

/**
 * Check the current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!isNotificationSupported()) {
    return 'denied'
  }
  return Notification.permission
}

/**
 * Check if notifications are enabled (granted permission)
 */
export function isNotificationEnabled(): boolean {
  return getNotificationPermission() === 'granted'
}

/**
 * Request notification permission from the user
 * Returns a promise that resolves to the permission status
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isNotificationSupported()) {
    console.warn('Browser does not support notifications')
    return 'denied'
  }

  try {
    const permission = await Notification.requestPermission()
    return permission
  } catch (error) {
    console.error('Error requesting notification permission:', error)
    return 'denied'
  }
}

/**
 * Map notification status to HTML5 notification options
 */
function getNotificationOptions(notification: NotificationBase): NotificationOptions {
  const options: NotificationOptions = {
    body: notification.message,
    icon: getIconForStatus(notification.status),
    tag: `notification-${Date.now()}`,
    requireInteraction: notification.status === NotificationStatus.error
  }

  if (notification.detail) {
    options.body = `${notification.message}\n${notification.detail}`
  }

  return options
}

/**
 * Get the appropriate icon for the notification status
 */
function getIconForStatus(status: string): string | undefined {
  // Return undefined to use browser default icons
  // In a production app, you might want to use absolute URLs to your icons
  switch (status) {
    case NotificationStatus.success:
      return undefined
    case NotificationStatus.warning:
      return undefined
    case NotificationStatus.error:
      return undefined
    case NotificationStatus.info:
      return undefined
    default:
      return undefined
  }
}

/**
 * Show a desktop notification using the HTML5 Notification API
 * Returns the Notification object if successful, null otherwise
 */
export function showDesktopNotification(notification: NotificationBase): Notification | null {
  if (!isNotificationSupported()) {
    console.warn('Browser does not support notifications')
    return null
  }

  if (!isNotificationEnabled()) {
    console.warn('Notifications are not enabled. Permission status:', getNotificationPermission())
    return null
  }

  try {
    const options = getNotificationOptions(notification)
    const desktopNotification = new Notification(notification.message, options)

    // Auto-close after 5 seconds for non-error notifications
    if (notification.status !== NotificationStatus.error) {
      setTimeout(() => {
        desktopNotification.close()
      }, 5000)
    }

    return desktopNotification
  } catch (error) {
    console.error('Error showing desktop notification:', error)
    return null
  }
}

/**
 * Show a desktop notification and handle click events
 */
export function showDesktopNotificationWithAction(
  notification: NotificationBase,
  onClick?: () => void,
  onClose?: () => void
): Notification | null {
  const desktopNotification = showDesktopNotification(notification)

  if (desktopNotification && onClick) {
    desktopNotification.onclick = () => {
      onClick()
      desktopNotification.close()
      // Focus the window when notification is clicked
      window.focus()
    }
  }

  if (desktopNotification && onClose) {
    desktopNotification.onclose = onClose
  }

  return desktopNotification
}